import { diffWords } from 'diff'
import DOMPurify from 'dompurify'

const BLOCK_TAGS = new Set([
  'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6',
  'UL', 'OL', 'LI',
  'TABLE', 'THEAD', 'TBODY', 'TR',
  'BLOCKQUOTE', 'PRE', 'HR', 'DIV', 'FIGURE',
  'IMG',
])

const VOID_TAGS = new Set(['HR', 'IMG', 'BR'])

function parseToBody(html: string): HTMLElement {
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const wrapper = doc.body.firstElementChild as HTMLElement | null
  return wrapper ?? doc.body
}

function getTopLevelBlocks(root: HTMLElement): HTMLElement[] {
  const blocks: HTMLElement[] = []
  for (const child of Array.from(root.children)) {
    if (child instanceof HTMLElement) blocks.push(child)
  }
  return blocks
}

function blockText(node: HTMLElement): string {
  return (node.textContent ?? '').replace(/\s+/g, ' ').trim()
}

function similarity(a: string, b: string): number {
  if (!a && !b) return 1
  if (!a || !b) return 0
  if (a === b) return 1
  const longer = a.length >= b.length ? a : b
  const shorter = a.length >= b.length ? b : a
  if (longer.length === 0) return 1
  // Token Jaccard — cheap and good enough for paragraph pairing.
  const aTokens = new Set(longer.toLowerCase().split(/\s+/).filter(Boolean))
  const bTokens = new Set(shorter.toLowerCase().split(/\s+/).filter(Boolean))
  if (aTokens.size === 0 && bTokens.size === 0) return 1
  let intersect = 0
  for (const t of bTokens) if (aTokens.has(t)) intersect++
  return intersect / Math.max(aTokens.size, bTokens.size)
}

interface PairOp {
  kind: 'pair' | 'remove' | 'insert'
  before?: HTMLElement
  after?: HTMLElement
}

/**
 * Pair before/after blocks using a greedy similarity match.
 * Sequential blocks with similarity >= threshold are paired and word-diffed.
 * Unmatched before-blocks become deletions; unmatched after-blocks become insertions.
 */
function pairBlocks(beforeBlocks: HTMLElement[], afterBlocks: HTMLElement[]): PairOp[] {
  const ops: PairOp[] = []
  let i = 0
  let j = 0
  const SIM_THRESHOLD = 0.35

  while (i < beforeBlocks.length || j < afterBlocks.length) {
    const b = beforeBlocks[i]
    const a = afterBlocks[j]

    if (!b && a) { ops.push({ kind: 'insert', after: a }); j++; continue }
    if (!a && b) { ops.push({ kind: 'remove', before: b }); i++; continue }
    if (!a || !b) break

    const bText = blockText(b)
    const aText = blockText(a)
    const sameTag = b.tagName === a.tagName

    // Identical or trivially similar — pair
    if (sameTag && bText === aText) { ops.push({ kind: 'pair', before: b, after: a }); i++; j++; continue }

    const directSim = sameTag ? similarity(bText, aText) : 0
    // Look ahead a small window to decide whether b matches a later after-block
    // (insertion before this point) or a matches a later before-block (deletion before this point).
    let bestAfterIdx = -1
    let bestAfterSim = directSim
    const lookahead = 3
    for (let k = j + 1; k <= j + lookahead && k < afterBlocks.length; k++) {
      const cand = afterBlocks[k]
      if (!cand || cand.tagName !== b.tagName) continue
      const sim = similarity(bText, blockText(cand))
      if (sim > bestAfterSim) { bestAfterSim = sim; bestAfterIdx = k }
    }
    let bestBeforeIdx = -1
    let bestBeforeSim = directSim
    for (let k = i + 1; k <= i + lookahead && k < beforeBlocks.length; k++) {
      const cand = beforeBlocks[k]
      if (!cand || cand.tagName !== a.tagName) continue
      const sim = similarity(aText, blockText(cand))
      if (sim > bestBeforeSim) { bestBeforeSim = sim; bestBeforeIdx = k }
    }

    if (bestAfterIdx > -1 && bestAfterSim > bestBeforeSim) {
      // Inserted block(s) before pairing
      while (j < bestAfterIdx) { ops.push({ kind: 'insert', after: afterBlocks[j]! }); j++ }
      ops.push({ kind: 'pair', before: b, after: afterBlocks[j]! })
      i++; j++
      continue
    }
    if (bestBeforeIdx > -1 && bestBeforeSim > directSim) {
      while (i < bestBeforeIdx) { ops.push({ kind: 'remove', before: beforeBlocks[i]! }); i++ }
      ops.push({ kind: 'pair', before: beforeBlocks[i]!, after: a })
      i++; j++
      continue
    }

    if (sameTag && directSim >= SIM_THRESHOLD) {
      ops.push({ kind: 'pair', before: b, after: a })
      i++; j++
    } else {
      ops.push({ kind: 'remove', before: b }); i++
      ops.push({ kind: 'insert', after: a }); j++
    }
  }

  return ops
}

// Merge threshold is deliberately higher than SIM_THRESHOLD (0.35): pairing is
// cheap to accept, but a wrong merge fabricates a "modified" hunk out of two
// unrelated blocks. Identical text always merges regardless of tag.
const MERGE_THRESHOLD = 0.5

/**
 * pairBlocks is tag-strict, so a block whose tag changes (heading level,
 * p → h2) becomes remove + insert even when its text is identical. Within each
 * run of consecutive remove/insert ops, greedily re-pair each insert with the
 * first sufficiently similar remaining remove, in order on both sides so the
 * before-order and after-order of the reconstruction are preserved.
 */
function coalesceOps(ops: PairOp[]): PairOp[] {
  const out: PairOp[] = []
  let i = 0
  while (i < ops.length) {
    if (ops[i]!.kind === 'pair') { out.push(ops[i]!); i++; continue }
    let end = i
    while (end < ops.length && ops[end]!.kind !== 'pair') end++
    out.push(...coalesceRun(ops.slice(i, end)))
    i = end
  }
  return out
}

function coalesceRun(run: PairOp[]): PairOp[] {
  const removes = run.filter((o) => o.kind === 'remove')
  const inserts = run.filter((o) => o.kind === 'insert')
  if (removes.length === 0 || inserts.length === 0) return run

  // insert index -> remove index, both strictly increasing (in-order greedy)
  const matches = new Map<number, number>()
  let rPtr = 0
  inserts.forEach((ins, ii) => {
    const aText = blockText(ins.after!)
    for (let ri = rPtr; ri < removes.length; ri++) {
      const bText = blockText(removes[ri]!.before!)
      if (bText === aText || similarity(bText, aText) >= MERGE_THRESHOLD) {
        matches.set(ii, ri)
        rPtr = ri + 1
        break
      }
    }
  })
  if (matches.size === 0) return run

  // Emit in after-order (inserts drive the sequence); unmatched removes are
  // interleaved at their original before-order positions so reject-all
  // reconstructs the before document verbatim.
  const out: PairOp[] = []
  let emitted = 0
  inserts.forEach((ins, ii) => {
    const ri = matches.get(ii)
    if (ri !== undefined) {
      while (emitted < ri) out.push(removes[emitted++]!)
      emitted = ri + 1
      out.push({ kind: 'pair', before: removes[ri]!.before!, after: ins.after! })
    } else {
      out.push(ins)
    }
  })
  while (emitted < removes.length) out.push(removes[emitted++]!)
  return out
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

/**
 * Word-level diff over plain text, emitting `<ins>` and `<del>` spans.
 */
function wordDiffHtml(before: string, after: string): string {
  const parts = diffWords(before, after)
  let out = ''
  for (const part of parts) {
    const value = escapeHtml(part.value)
    if (part.added) out += `<ins>${value}</ins>`
    else if (part.removed) out += `<del>${value}</del>`
    else out += value
  }
  return out
}

function nodesEqual(a: ChildNode, b: ChildNode): boolean {
  if (a.nodeType !== b.nodeType) return false
  if (a.nodeType === Node.TEXT_NODE) return (a.textContent ?? '') === (b.textContent ?? '')
  if (a.nodeType === Node.ELEMENT_NODE) {
    const ae = a as Element
    const be = b as Element
    if (ae.tagName !== be.tagName) return false
    return ae.outerHTML === be.outerHTML
  }
  return false
}

/**
 * Diff the inline content of a paired block by walking children.
 * Text runs are word-diffed; non-text children (links, images, code spans)
 * are emitted as-is when identical, otherwise wrapped del/ins.
 */
function diffInlineChildren(beforeEl: HTMLElement, afterEl: HTMLElement): string {
  const beforeChildren = Array.from(beforeEl.childNodes)
  const afterChildren = Array.from(afterEl.childNodes)

  // Fast path: identical inline content
  if (beforeEl.innerHTML === afterEl.innerHTML) return afterEl.innerHTML

  // Common case: pure text change → word-diff the text content
  const beforeAllText = beforeChildren.every((n) => n.nodeType === Node.TEXT_NODE)
  const afterAllText = afterChildren.every((n) => n.nodeType === Node.TEXT_NODE)
  if (beforeAllText && afterAllText) {
    return wordDiffHtml(beforeEl.textContent ?? '', afterEl.textContent ?? '')
  }

  // Mixed inline content (links, strong, em, code, etc.) — align by sequential
  // identity. Identical adjacent children pass through; runs of differing
  // children are word-diffed on their text projection so the output stays
  // visually meaningful even if some inline formatting is collapsed in the diff.
  let i = 0
  let j = 0
  let out = ''
  const renderRun = (beforeRun: ChildNode[], afterRun: ChildNode[]): string => {
    if (beforeRun.length === 0 && afterRun.length === 0) return ''
    const beforeHtml = beforeRun.map((n) => (n as Element).outerHTML ?? n.textContent ?? '').join('')
    const afterHtml = afterRun.map((n) => (n as Element).outerHTML ?? n.textContent ?? '').join('')
    const beforeText = beforeRun.map((n) => n.textContent ?? '').join('')
    const afterText = afterRun.map((n) => n.textContent ?? '').join('')
    const allText = [...beforeRun, ...afterRun].every((n) => n.nodeType === Node.TEXT_NODE)
    if (allText) return wordDiffHtml(beforeText, afterText)
    let segment = ''
    if (beforeHtml) segment += `<del>${beforeHtml}</del>`
    if (afterHtml) segment += `<ins>${afterHtml}</ins>`
    return segment
  }

  let beforeRun: ChildNode[] = []
  let afterRun: ChildNode[] = []
  while (i < beforeChildren.length || j < afterChildren.length) {
    const bn = beforeChildren[i]
    const an = afterChildren[j]
    if (bn && an && nodesEqual(bn, an)) {
      out += renderRun(beforeRun, afterRun)
      beforeRun = []; afterRun = []
      out += an.nodeType === Node.ELEMENT_NODE ? (an as Element).outerHTML : escapeHtml(an.textContent ?? '')
      i++; j++
      continue
    }
    if (bn) { beforeRun.push(bn); i++ }
    if (an) { afterRun.push(an); j++ }
  }
  out += renderRun(beforeRun, afterRun)
  return out
}

function copyAttrs(target: HTMLElement, source: HTMLElement): void {
  for (const attr of Array.from(source.attributes)) {
    target.setAttribute(attr.name, attr.value)
  }
}

function diffPairedBlock(before: HTMLElement, after: HTMLElement): string {
  const tag = after.tagName
  // Void elements (hr, img) — if attributes differ, render as remove+insert.
  if (VOID_TAGS.has(tag)) {
    if (before.outerHTML === after.outerHTML) return after.outerHTML
    return `<del>${before.outerHTML}</del><ins>${after.outerHTML}</ins>`
  }

  // Tables → diff row-by-row recursively.
  if (tag === 'TABLE') {
    return diffTable(before, after)
  }

  // Lists → diff item-by-item using the same pairing.
  if (tag === 'UL' || tag === 'OL') {
    return diffList(before, after)
  }

  // Generic block: preserve outer tag + attributes; diff inline children.
  const inner = diffInlineChildren(before, after)
  const out = document.createElement(tag.toLowerCase())
  copyAttrs(out, after)
  out.innerHTML = inner
  return out.outerHTML
}

function diffList(before: HTMLElement, after: HTMLElement): string {
  const beforeItems = Array.from(before.children).filter((c) => c.tagName === 'LI') as HTMLElement[]
  const afterItems = Array.from(after.children).filter((c) => c.tagName === 'LI') as HTMLElement[]
  const ops = pairBlocks(beforeItems, afterItems)
  let inner = ''
  for (const op of ops) {
    if (op.kind === 'pair' && op.before && op.after) {
      const childInner = diffInlineChildren(op.before, op.after)
      inner += `<li>${childInner}</li>`
    } else if (op.kind === 'remove' && op.before) {
      inner += `<li><del>${op.before.innerHTML}</del></li>`
    } else if (op.kind === 'insert' && op.after) {
      inner += `<li><ins>${op.after.innerHTML}</ins></li>`
    }
  }
  const out = document.createElement(after.tagName.toLowerCase())
  copyAttrs(out, after)
  out.innerHTML = inner
  return out.outerHTML
}

function diffTable(before: HTMLElement, after: HTMLElement): string {
  // Flatten rows from thead/tbody so we can diff in document order.
  const rows = (root: HTMLElement): HTMLElement[] => {
    const result: HTMLElement[] = []
    root.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tr').forEach((r) => {
      result.push(r as HTMLElement)
    })
    return result
  }
  const beforeRows = rows(before)
  const afterRows = rows(after)
  const ops = pairBlocks(beforeRows, afterRows)

  let body = ''
  for (const op of ops) {
    if (op.kind === 'pair' && op.before && op.after) {
      const beforeCells = Array.from(op.before.children) as HTMLElement[]
      const afterCells = Array.from(op.after.children) as HTMLElement[]
      const cellOps = pairBlocks(beforeCells, afterCells)
      let rowInner = ''
      for (const cop of cellOps) {
        if (cop.kind === 'pair' && cop.before && cop.after) {
          const inner = diffInlineChildren(cop.before, cop.after)
          const cell = document.createElement(cop.after.tagName.toLowerCase())
          copyAttrs(cell, cop.after)
          cell.innerHTML = inner
          rowInner += cell.outerHTML
        } else if (cop.kind === 'remove' && cop.before) {
          const cell = document.createElement(cop.before.tagName.toLowerCase())
          copyAttrs(cell, cop.before)
          cell.innerHTML = `<del>${cop.before.innerHTML}</del>`
          rowInner += cell.outerHTML
        } else if (cop.kind === 'insert' && cop.after) {
          const cell = document.createElement(cop.after.tagName.toLowerCase())
          copyAttrs(cell, cop.after)
          cell.innerHTML = `<ins>${cop.after.innerHTML}</ins>`
          rowInner += cell.outerHTML
        }
      }
      const row = document.createElement('tr')
      copyAttrs(row, op.after)
      row.innerHTML = rowInner
      body += row.outerHTML
    } else if (op.kind === 'remove' && op.before) {
      // Wrap each cell's content in <del> so the whole row reads as deleted.
      const row = document.createElement('tr')
      copyAttrs(row, op.before)
      const cells = Array.from(op.before.children) as HTMLElement[]
      row.innerHTML = cells.map((c) => {
        const cell = document.createElement(c.tagName.toLowerCase())
        copyAttrs(cell, c)
        cell.innerHTML = `<del>${c.innerHTML}</del>`
        return cell.outerHTML
      }).join('')
      body += row.outerHTML
    } else if (op.kind === 'insert' && op.after) {
      const row = document.createElement('tr')
      copyAttrs(row, op.after)
      const cells = Array.from(op.after.children) as HTMLElement[]
      row.innerHTML = cells.map((c) => {
        const cell = document.createElement(c.tagName.toLowerCase())
        copyAttrs(cell, c)
        cell.innerHTML = `<ins>${c.innerHTML}</ins>`
        return cell.outerHTML
      }).join('')
      body += row.outerHTML
    }
  }

  const table = document.createElement('table')
  copyAttrs(table, after)
  // Preserve tbody wrapper for editor parity.
  table.innerHTML = `<tbody>${body}</tbody>`
  return table.outerHTML
}

function wrapBlockWithMark(el: HTMLElement, mark: 'ins' | 'del'): string {
  const tag = el.tagName
  if (VOID_TAGS.has(tag)) {
    return `<${mark}>${el.outerHTML}</${mark}>`
  }
  // For block elements we wrap the *inner* content with the mark instead of the
  // whole block — TipTap's schema accepts marks only on inline content, so an
  // outer `<ins><p>...</p></ins>` would be dropped on parse.
  const cloned = el.cloneNode(false) as HTMLElement
  if (tag === 'TABLE') {
    // Wrap each cell's inner.
    cloned.innerHTML = wrapTableInner(el, mark)
  } else if (tag === 'UL' || tag === 'OL') {
    const items = Array.from(el.children).filter((c) => c.tagName === 'LI') as HTMLElement[]
    cloned.innerHTML = items.map((li) => `<li><${mark}>${li.innerHTML}</${mark}></li>`).join('')
  } else {
    cloned.innerHTML = `<${mark}>${el.innerHTML}</${mark}>`
  }
  return cloned.outerHTML
}

function wrapTableInner(table: HTMLElement, mark: 'ins' | 'del'): string {
  const rows: string[] = []
  table.querySelectorAll(':scope > thead > tr, :scope > tbody > tr, :scope > tr').forEach((r) => {
    const row = r as HTMLElement
    const cells = Array.from(row.children) as HTMLElement[]
    const newRow = document.createElement('tr')
    copyAttrs(newRow, row)
    newRow.innerHTML = cells.map((c) => {
      const cell = document.createElement(c.tagName.toLowerCase())
      copyAttrs(cell, c)
      cell.innerHTML = `<${mark}>${c.innerHTML}</${mark}>`
      return cell.outerHTML
    }).join('')
    rows.push(newRow.outerHTML)
  })
  return `<tbody>${rows.join('')}</tbody>`
}

/** Tag every root element of a hunk's diff fragment so the editor can address
 * it (scroll-to, future per-hunk UI). Fragments can have multiple roots (void
 * element diffs emit sibling <del>/<ins>), so stamp all of them. */
function stampHunkId(fragmentHtml: string, hunkId: string): string {
  const tpl = document.createElement('template')
  tpl.innerHTML = fragmentHtml
  Array.from(tpl.content.children).forEach((el) => el.setAttribute('data-hunk-id', hunkId))
  return tpl.innerHTML
}

/**
 * Build a diff representation as HTML, preserving block structure (tables,
 * lists, headings) and using <ins>/<del> marks for inline word-level changes.
 *
 * The output is parseable by the TipTap editor as long as the Insertion and
 * Deletion marks (diffMarks.ts) are registered.
 */
export function buildSegmentDiffHtml(beforeHtml: string, afterHtml: string): string {
  return buildSegmentDiff(beforeHtml, afterHtml).html
}

/**
 * Track 3: structured diff that supports per-hunk accept/reject. Returns the
 * same HTML string as `buildSegmentDiffHtml` plus the ordered segment list
 * needed to rebuild the final HTML when the user accepts some hunks and
 * rejects others.
 */
export interface DiffHunk {
  id: string
  kind: 'modified' | 'removed' | 'inserted'
  beforeHtml: string
  afterHtml: string
}

export interface DiffSegment {
  index: number
  /** Either an unchanged passthrough block or a hunk the user can decide on. */
  kind: 'unchanged' | 'hunk'
  /** kind === 'unchanged': verbatim HTML to keep regardless of decisions. */
  html?: string
  /** kind === 'hunk': stable id, before/after states, and the diff fragment. */
  hunkId?: string
  hunkKind?: 'modified' | 'removed' | 'inserted'
  beforeHtml?: string
  afterHtml?: string
  diffHtml?: string
}

export interface SegmentDiff {
  /** Concatenated diff HTML for editor rendering. */
  html: string
  /** Sequential segments — drives partial-accept reconstruction. */
  segments: DiffSegment[]
  /** Just the hunks, for inline UI controls. */
  hunks: DiffHunk[]
}

export type HunkDecision = 'accepted' | 'rejected' | 'pending'

export function buildSegmentDiff(beforeHtml: string, afterHtml: string): SegmentDiff {
  // Both inputs are LLM/user-authored HTML. Strip scripts, event-handler
  // attributes, and javascript: URLs up front so every downstream consumer
  // of the diff HTML (editor, persisted segment content) receives safe markup.
  if (typeof window !== 'undefined') {
    beforeHtml = DOMPurify.sanitize(beforeHtml)
    afterHtml = DOMPurify.sanitize(afterHtml)
  }

  if (typeof DOMParser === 'undefined') {
    return {
      html: afterHtml,
      segments: [{ index: 0, kind: 'unchanged', html: afterHtml }],
      hunks: [],
    }
  }
  if (beforeHtml === afterHtml) {
    return {
      html: afterHtml,
      segments: [{ index: 0, kind: 'unchanged', html: afterHtml }],
      hunks: [],
    }
  }

  const beforeRoot = parseToBody(beforeHtml || '')
  const afterRoot = parseToBody(afterHtml || '')

  const beforeBlocks = getTopLevelBlocks(beforeRoot)
  const afterBlocks = getTopLevelBlocks(afterRoot)

  // No structured blocks → fall back to inline diff. The whole thing is one
  // hunk because the user can only accept or reject the whole change.
  if (beforeBlocks.length === 0 && afterBlocks.length === 0) {
    const before = beforeRoot.textContent ?? ''
    const after = afterRoot.textContent ?? ''
    const diffHtml = `<p>${wordDiffHtml(before, after)}</p>`
    const hunkId = 'hunk-0'
    return {
      html: diffHtml,
      segments: [
        {
          index: 0,
          kind: 'hunk',
          hunkId,
          hunkKind: 'modified',
          beforeHtml: `<p>${escapeHtml(before)}</p>`,
          afterHtml: `<p>${escapeHtml(after)}</p>`,
          diffHtml,
        },
      ],
      hunks: [{ id: hunkId, kind: 'modified', beforeHtml: before, afterHtml: after }],
    }
  }

  const ops = coalesceOps(pairBlocks(beforeBlocks, afterBlocks))
  const segments: DiffSegment[] = []
  const hunks: DiffHunk[] = []
  let html = ''
  let hunkCounter = 0

  for (const op of ops) {
    const segIndex = segments.length
    if (op.kind === 'pair' && op.before && op.after) {
      const beforeOuter = op.before.outerHTML
      const afterOuter = op.after.outerHTML
      // Identical paired block — passthrough, no hunk.
      if (beforeOuter === afterOuter) {
        segments.push({ index: segIndex, kind: 'unchanged', html: afterOuter })
        html += afterOuter
        continue
      }
      const hunkId = `hunk-${hunkCounter++}`
      const diffFragment = stampHunkId(diffPairedBlock(op.before, op.after), hunkId)
      segments.push({
        index: segIndex,
        kind: 'hunk',
        hunkId,
        hunkKind: 'modified',
        beforeHtml: beforeOuter,
        afterHtml: afterOuter,
        diffHtml: diffFragment,
      })
      hunks.push({ id: hunkId, kind: 'modified', beforeHtml: beforeOuter, afterHtml: afterOuter })
      html += diffFragment
    } else if (op.kind === 'remove' && op.before) {
      if (!(BLOCK_TAGS.has(op.before.tagName) || op.before.textContent?.trim())) continue
      const beforeOuter = op.before.outerHTML
      const hunkId = `hunk-${hunkCounter++}`
      const diffFragment = stampHunkId(wrapBlockWithMark(op.before, 'del'), hunkId)
      segments.push({
        index: segIndex,
        kind: 'hunk',
        hunkId,
        hunkKind: 'removed',
        beforeHtml: beforeOuter,
        afterHtml: '',
        diffHtml: diffFragment,
      })
      hunks.push({ id: hunkId, kind: 'removed', beforeHtml: beforeOuter, afterHtml: '' })
      html += diffFragment
    } else if (op.kind === 'insert' && op.after) {
      if (!(BLOCK_TAGS.has(op.after.tagName) || op.after.textContent?.trim())) continue
      const afterOuter = op.after.outerHTML
      const hunkId = `hunk-${hunkCounter++}`
      const diffFragment = stampHunkId(wrapBlockWithMark(op.after, 'ins'), hunkId)
      segments.push({
        index: segIndex,
        kind: 'hunk',
        hunkId,
        hunkKind: 'inserted',
        beforeHtml: '',
        afterHtml: afterOuter,
        diffHtml: diffFragment,
      })
      hunks.push({ id: hunkId, kind: 'inserted', beforeHtml: '', afterHtml: afterOuter })
      html += diffFragment
    }
  }

  return { html, segments, hunks }
}

/**
 * Rebuild the final segment HTML from a per-hunk decision map. Used when the
 * user has accepted some hunks and rejected others — the editor saves the
 * resulting HTML to the segment instead of all-or-nothing accept/reject.
 *
 * Pending hunks default to `defaultDecision` so the caller can express both
 * "Accept all (and any pending)" and "Reject all (and any pending)" semantics.
 */
export function applyHunkDecisions(
  segments: DiffSegment[],
  decisions: Record<string, HunkDecision>,
  defaultDecision: 'accepted' | 'rejected' = 'accepted',
): string {
  let out = ''
  for (const seg of segments) {
    if (seg.kind === 'unchanged') {
      out += seg.html ?? ''
      continue
    }
    const id = seg.hunkId
    if (!id) continue
    const raw = decisions[id] ?? 'pending'
    const effective = raw === 'pending' ? defaultDecision : raw
    if (effective === 'accepted') {
      // Accepted: keep the after state. Removed hunks contribute nothing.
      if (seg.hunkKind !== 'removed') {
        out += seg.afterHtml ?? ''
      }
    } else {
      // Rejected: revert to the before state. Inserted hunks contribute nothing.
      if (seg.hunkKind !== 'inserted') {
        out += seg.beforeHtml ?? ''
      }
    }
  }
  return out
}

export function countDiffWords(html: string): { added: number; removed: number } {
  if (typeof DOMParser === 'undefined') return { added: 0, removed: 0 }
  const root = parseToBody(html)
  const count = (selector: string): number => {
    let total = 0
    root.querySelectorAll(selector).forEach((el) => {
      const text = (el.textContent ?? '').trim()
      if (!text) return
      total += text.split(/\s+/).filter(Boolean).length
    })
    return total
  }
  return { added: count('ins'), removed: count('del') }
}
